import { useContentDStore } from '@/stores/useContentDStore'
import { FileText, PenLine } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Typography from '@tiptap/extension-typography'
import Placeholder from '@tiptap/extension-placeholder'
import { marked } from 'marked'

// Configure marked for GFM
marked.use({
  breaks: true,
  gfm: true,
})

type TabType = 'document' | 'foolscap'

/**
 * ContentDDocumentPanel - Document panel for the Content Drafter workflow.
 *
 * Shows two tabs:
 * 1. Document - The drafted document (editable)
 * 2. Foolscap - Read-only reference of the source Content Foolscap (rendered via TipTap)
 */
export function ContentDDocumentPanel() {
  const {
    sessionId,
    documentContent,
    foolscapSnapshot,
  } = useContentDStore()

  const [activeTab, setActiveTab] = useState<TabType>('foolscap')

  // Reset to foolscap tab when session changes (Foolscap is the reference)
  // But only if we have a foolscap - otherwise show document tab
  useEffect(() => {
    setActiveTab(foolscapSnapshot ? 'foolscap' : 'document')
  }, [sessionId, foolscapSnapshot])

  // Convert Foolscap markdown to HTML for TipTap
  const foolscapHtml = useMemo(() => {
    if (!foolscapSnapshot) return ''
    return marked.parse(foolscapSnapshot, { async: false }) as string
  }, [foolscapSnapshot])

  // TipTap editor for Foolscap (read-only)
  const foolscapEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        horizontalRule: {},
        bulletList: {},
        listItem: {},
      }),
      Placeholder.configure({
        placeholder: 'Content Foolscap reference...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
    ],
    content: foolscapHtml,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose-z outline-none min-h-full',
      },
    },
    immediatelyRender: false,
  }, [])

  // Update Foolscap editor when content changes
  useEffect(() => {
    if (foolscapEditor && foolscapHtml) {
      foolscapEditor.commands.setContent(foolscapHtml, { emitUpdate: false })
    }
  }, [foolscapEditor, foolscapHtml])

  // Convert document markdown to HTML for TipTap
  const documentHtml = useMemo(() => {
    if (!documentContent) return ''
    return marked.parse(documentContent, { async: false }) as string
  }, [documentContent])

  // TipTap editor for Draft document (read-only for now)
  const documentEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        horizontalRule: {},
        bulletList: {},
        listItem: {},
      }),
      Placeholder.configure({
        placeholder: 'Your draft will appear here...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
    ],
    content: documentHtml,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose-z outline-none min-h-full',
      },
    },
    immediatelyRender: false,
  }, [])

  // Update document editor when content changes
  useEffect(() => {
    if (documentEditor && documentHtml) {
      documentEditor.commands.setContent(documentHtml, { emitUpdate: false })
    }
  }, [documentEditor, documentHtml])

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-3 md:px-2 py-2 md:py-1.5 border-b border-border overflow-x-auto">
        {/* Draft tab */}
        <button
          onClick={() => setActiveTab('document')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'document'
              ? 'bg-muted/50 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          }`}
        >
          <PenLine className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Draft</span>
        </button>

        {/* Foolscap reference tab - only show when snapshot exists */}
        {foolscapSnapshot && (
          <button
            onClick={() => setActiveTab('foolscap')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
              activeTab === 'foolscap'
                ? 'bg-muted/50 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Foolscap</span>
            <span className="ml-1 text-xs text-muted-foreground">(reference)</span>
          </button>
        )}
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden relative">
        {/* Draft document */}
        <div className={`absolute inset-0 ${activeTab === 'document' ? '' : 'invisible pointer-events-none'}`}>
          <div className="h-full overflow-auto px-4 py-4">
            {documentContent ? (
              <div className="min-h-full">
                <EditorContent editor={documentEditor} className="min-h-full" />
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <PenLine className="w-8 h-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  <p className="text-sm font-light">Your drafted document will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Foolscap reference (read-only, rendered via TipTap) */}
        <div className={`absolute inset-0 ${activeTab === 'foolscap' ? '' : 'invisible pointer-events-none'}`}>
          <div className="h-full overflow-auto px-4 py-4">
            {foolscapSnapshot ? (
              <div className="min-h-full">
                <EditorContent editor={foolscapEditor} className="min-h-full" />
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  <p className="text-sm font-light">No Content Foolscap loaded</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
